import {B2BCheckoutTabs, B2BScreenParam} from '../types';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useColorThemeType, useShadow} from '@buncha/appStyles/hooks/theme';
import {useIsFocused, useRoute} from '@react-navigation/native';

import {APIV1Version} from '@buncha/common/types/common';
import {Analytics} from '@buncha/config/analytics/analytics';
import Approval from '../components/Approval';
import {B2BCheckoutContext} from '../context/B2BCheckoutContext';
import {B2BScreens} from '../navigation/types';
import {Box} from '@buncha/components/core/view';
import {Divider} from 'native-base';
import Header from '@buncha/components/composites/Header/Header';
import ScreenHeader from '@buncha/common/components/screenHeader';
import {StyleSheet} from 'react-native';
import SupportAgent from '@buncha/components/core/icon/SupportAgent';
import Tab from '@buncha/components/composites/Tabs/Tab';
import {TabNode} from '@buncha/components/shared/TabNode';
import {Touchable} from '@buncha/components/core/touchable';
import Verification from '../components/Verification';
import {useB2BCheckoutReducer} from '../hooks/B2BCheckoutReducer';
import {useGetOrdersForValidationAndUpdationCheckout} from '../hooks/B2BChekout';
import {Modules} from '@buncha/utils/types';
import {useGetLocale} from '@buncha/localization/hooks';

const B2BCheckout: React.FC = () => {
  const [currentTab, setCurrentTab] = useState<string>();
  const routeParams = useRoute()?.params as B2BScreenParam | undefined;
  const [state, dispatch] = useB2BCheckoutReducer();
  const [getOrdersForValidationAndUpdation, checkoutState, initPage] =
    useGetOrdersForValidationAndUpdationCheckout(APIV1Version.V100, dispatch);
  const provider = useMemo(() => ({state, dispatch}), [dispatch, state]);
  const {title, body} = useGetLocale(Modules.B2B, B2BScreens.B2BCheckout) ?? {};
  const isFocus = useIsFocused();
  const theme = useColorThemeType();
  const shadow = useShadow('2');

  const runId = routeParams?.runId;
  const storeId = routeParams?.storeId;
  const storeIndex = routeParams?.storeIndex;
  const verificationItems = state.ordersForValidation;
  const updationItemItems = state.ordersForUpdation ?? [];
  const keyRef = useRef('');
  const sections: {[key: string]: React.ReactElement} = {};
  const tabHeader = [B2BCheckoutTabs.verification, B2BCheckoutTabs.approval];
  const tabData = useMemo(() => {
    return [
      {
        key: B2BCheckoutTabs.verification,
        data: verificationItems?.length
          ? body?.verify?.[1]?.replace(
              '{verificationItems}',
              verificationItems?.length,
            )
          : body?.verify?.[0],
      },
      {
        key: B2BCheckoutTabs.approval,
        data: updationItemItems?.length
          ? body?.approval?.[1]?.replace(
              '{updationItemItems}',
              updationItemItems?.length,
            )
          : body?.approval?.[0],
      },
    ];
  }, [
    body?.approval,
    body?.verify,
    updationItemItems?.length,
    verificationItems?.length,
  ]);

  tabHeader.forEach(tab => {
    switch (tab) {
      case B2BCheckoutTabs.verification:
        sections[tab] = (
          <Verification
            loading={checkoutState.loading}
            storeId={storeId}
            storeIndex={storeIndex ?? ''}
          />
        );
        break;
      case B2BCheckoutTabs.approval:
        sections[tab] = <Approval loading={checkoutState.loading} />;
        break;
      default:
        sections[B2BCheckoutTabs.verification] = (
          <Verification
            loading={checkoutState.loading}
            storeId={storeId}
            storeIndex={storeIndex ?? ''}
          />
        );
    }
  });

  const tabNode = useCallback((info: any, activeKey: any) => {
    return <TabNode info={info} activeKey={activeKey} />;
  }, []);

  const openIntercom = useCallback(() => {
    return Analytics.displayMessenger();
  }, []);

  useEffect(() => {
    if (runId && isFocus) getOrdersForValidationAndUpdation(runId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runId, currentTab, isFocus]);

  useEffect(() => {
    if (!initPage)
      if (verificationItems?.length) {
        if (!updationItemItems?.length)
          setCurrentTab(B2BCheckoutTabs.verification);
      } else {
        setCurrentTab(B2BCheckoutTabs.approval);
      }
  }, [verificationItems, initPage, updationItemItems?.length]);

  return (
    <B2BCheckoutContext.Provider value={provider}>
      <Header
        loading={checkoutState.loading && !currentTab?.length}
        showDefaultLeftHeader>
        <ScreenHeader title={title?.meijerCheckout} />
        <Touchable
          style={[
            styles.intercomIcon,
            {
              backgroundColor: theme?.background?.yellow?.[200],
              ...shadow,
            },
          ]}
          onPress={openIntercom}>
          <SupportAgent
            width={30}
            height={30}
            viewBox={'0 0 20 20'}
            fill={theme?.icon.blue[300]}
          />
        </Touchable>
        <Divider style={styles.topDivider} />
        <Box style={styles.container}>
          <Tab
            key={keyRef.current}
            activeKey={currentTab}
            tabData={tabData}
            sections={sections}
            style={styles.tabContainer}
            tabNode={tabNode}
            setActiveKey={setCurrentTab}
          />
        </Box>
      </Header>
    </B2BCheckoutContext.Provider>
  );
};

export default React.memo(B2BCheckout);

const styles = StyleSheet.create({
  tabContainer: {
    marginTop: 15,
    borderWidth: 2,
  },
  topDivider: {marginTop: 5, marginBottom: 10},
  container: {flex: 1, marginHorizontal: 10},
  intercomIcon: {
    position: 'absolute',
    top: 10,
    right: 10,
    borderRadius: 100,
    padding: 4,
    elevation: 20,
  },
});
